/**
 * 표 클립보드 (스프레드시트식 구조 보존 복사/붙여넣기) — 순수, UI 무관.
 * 스펙: docs/table-clipboard-spec.md
 *
 * 클립 포맷 = 기존 BlockNote `tableContent`(별도 스키마 없이 직렬화 계층 재사용).
 *  - extractGridClip: 표 모델의 부분 사각형 → tableContent(경계 가로지르는 병합은 잘림)
 *  - gridClipToHtml / gridClipToTSV: 외부(Excel/Sheets) 상호운용용 내보내기
 *  - parseClipToGrid: 클립보드(lumir-JSON / HTML <table> / TSV) → tableContent | null
 *  - pasteGridIntoModel: 앵커 셀부터 그리드로 채움(오버플로 자동확장·대상병합 해제·소스병합 보존)
 */

import { newMaster, normalize } from "./model.js";
import { insertRow, insertCol, splitCell, expandRect } from "./ops.js";
import { modelToTableContent, tableContentToModel } from "../serialize/table.js";
import { blocksToHtml } from "../serialize/html.js";
import { parseHtmlToBlocks } from "../paste/paste-html.js";
import { normalizeExcelClipboardHtml } from "../paste/excel-format.js";

export const LUMIR_TABLE_MIME = "application/x-lumir-table";

// 셀에서 왕복 보존하는 prop 키(serialize/table.js와 동일 목록)
const CELL_PROP_KEYS = ["backgroundColor", "textColor", "textAlignment", "verticalAlignment", "rowHeight"];

const deepContent = (content) => (Array.isArray(content) ? JSON.parse(JSON.stringify(content)) : []);

/** master 복제(내용 딥카피 + 셀 prop 복사). 새 id 부여. */
function cloneMaster(src) {
  const nm = newMaster({ content: deepContent(src && src.content) });
  if (src) for (const k of CELL_PROP_KEYS) if (src[k] !== undefined) nm[k] = src[k];
  return nm;
}

/** 인라인 콘텐츠 → 평문(TSV/평문 클립보드용). link는 내부 content 재귀. */
function inlineText(content) {
  if (!Array.isArray(content)) return "";
  return content.map((n) => (n && n.type === "link" ? inlineText(n.content) : (n && n.text) || "")).join("");
}

/**
 * 모델의 부분 사각형을 클립(tableContent)으로 추출. rect는 병합확장(expandRect) 완료 가정.
 * 경계를 가로지르는 병합은 안쪽 슬롯만 남아 normalize가 span을 자동 축소(잘림).
 */
export function extractGridClip(model, rect) {
  const G = model.G || [];
  const R = rect.r2 - rect.r1 + 1, C = rect.c2 - rect.c1 + 1;
  const seen = new Map(); // old master -> new master (병합 공유 유지)
  const subG = [];
  for (let r = 0; r < R; r++) {
    subG[r] = [];
    for (let c = 0; c < C; c++) {
      const om = G[rect.r1 + r] && G[rect.r1 + r][rect.c1 + c];
      let nm = om ? seen.get(om) : null;
      if (!nm) { nm = cloneMaster(om); if (om) seen.set(om, nm); }
      subG[r][c] = nm;
    }
  }
  const widths = Array.isArray(model.columnWidths)
    ? Array.from({ length: C }, (_, i) => (model.columnWidths[rect.c1 + i] != null ? model.columnWidths[rect.c1 + i] : null))
    : new Array(C).fill(null);
  const clipModel = { G: subG, columnWidths: widths };
  normalize(clipModel);
  return modelToTableContent(clipModel);
}

/** 클립(tableContent) → HTML `<table>` 문자열(외부 붙여넣기 상호운용).
 * 무손실 복원용 클립 JSON을 표 태그에 data-lumir-clip 으로 임베드한다(외부 앱은 무시,
 * 에디터 자체 붙여넣기는 parseClipToGrid가 이 값을 우선 사용해 색/열폭/헤더까지 완전 복원). */
export function gridClipToHtml(clip) {
  const html = blocksToHtml([{ type: "table", props: {}, content: clip, children: [] }]);
  try {
    const json = encodeURIComponent(JSON.stringify(clip)); // 따옴표/<>/& 없는 안전 문자열
    return html.replace(/<table\b/i, `<table data-lumir-clip="${json}"`);
  } catch (_) {
    return html;
  }
}

/** 클립(tableContent) → TSV(행 \n, 셀 \t). 병합/서식은 소실(평문 폴백). */
export function gridClipToTSV(clip) {
  const rows = (clip && clip.rows) || [];
  return rows
    .map((row) => (row.cells || []).map((cell) => inlineText(cell.content).replace(/[\t\r\n]+/g, " ")).join("\t"))
    .join("\n");
}

/** TSV 문자열 → tableContent(각 셀 평문 1x1, ragged는 최대 열수로 패딩). */
function tsvToTableContent(text) {
  const lines = String(text).replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n");
  const rows = lines.map((line) => line.split("\t"));
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  if (!cols) return null;
  return {
    type: "tableContent",
    columnWidths: new Array(cols).fill(null),
    rows: rows.map((cells) => ({
      cells: Array.from({ length: cols }, (_, i) => ({
        type: "tableCell",
        props: { colspan: 1, rowspan: 1 },
        content: cells[i] ? [{ type: "text", text: cells[i], styles: {} }] : [],
      })),
    })),
  };
}

/**
 * 클립보드 페이로드 → 클립(tableContent) | null. 우선순위: lumir-JSON > HTML <table> > TSV.
 * @param {{lumir?:string, html?:string, text?:string}} payload
 */
export function parseClipToGrid(payload) {
  const { lumir, html, text } = payload || {};
  if (lumir) {
    try {
      const tc = JSON.parse(lumir);
      if (tc && tc.type === "tableContent" && Array.isArray(tc.rows)) return tc;
    } catch (_) { /* fallthrough */ }
  }
  if (html) {
    // 에디터 자체 복사: gridClipToHtml이 임베드한 무손실 클립을 우선 복원(HTML 파싱 손실 회피).
    const m = html.match(/<table\b[^>]*\bdata-lumir-clip="([^"]*)"/i);
    if (m) {
      try {
        const tc = JSON.parse(decodeURIComponent(m[1]));
        if (tc && tc.type === "tableContent" && Array.isArray(tc.rows)) return tc;
      } catch (_) { /* fallthrough */ }
    }
  }
  if (html && /<table[\s>]/i.test(html)) {
    try {
      // 문단 붙여넣기(editor.js)와 동일하게 클래스 기반 색(PPT·데스크톱 Excel의 <style>.cls{background})을
      // computed 정규화로 data-*에 구운 뒤 파싱한다. 이 단계가 없으면 셀 붙여넣기에서 배경/글자색이 누락된다.
      const blocks = parseHtmlToBlocks(normalizeExcelClipboardHtml(html));
      const t = blocks.find((b) => b.type === "table");
      if (t && t.content) return t.content;
    } catch (_) { /* fallthrough */ }
  }
  if (text && /\t|\n/.test(text)) return tsvToTableContent(text);
  return null;
}

/**
 * 앵커 셀부터 클립 그리드로 대상 모델을 채운다(제자리 변형).
 *  - 오버플로: insertRow/insertCol 로 대상 자동 확장
 *  - 대상 병합: 붙임 영역과 겹치는 병합 해제(splitCell)
 *  - 소스 병합: 공유 master 로 보존
 *  - opts.maxCols: 열 자동확장 상한(에디터 가용폭 예산). 초과 열은 잘림(세로/행은 무제한). 0/미지정=무제한.
 * @returns {boolean} 채움 성공 여부
 */
export function pasteGridIntoModel(model, anchor, clip, opts) {
  const src = tableContentToModel(clip);
  const sR = src.G.length, sCsrc = src.G[0] ? src.G[0].length : 0;
  if (!sR || !sCsrc) return false;
  const ar = Math.max(0, anchor.r | 0), ac = Math.max(0, anchor.c | 0);
  const maxCols = opts && opts.maxCols > 0 ? opts.maxCols : Infinity;
  const curCols = () => (model.G[0] ? model.G[0].length : 0);

  // 1) 자동 확장 — 행은 전부, 열은 예산(maxCols) 내에서만. 예산 초과 소스 열은 잘림(표가 편집영역을 벗어나지 않도록).
  while (model.G.length < ar + sR) insertRow(model, model.G.length);
  const wantCols = Math.min(ac + sCsrc, maxCols);
  while (curCols() < wantCols) insertCol(model, curCols());
  const sC = Math.max(0, Math.min(sCsrc, curCols() - ac)); // 예산 내 실제 붙일 열 수
  if (sC < 1) { normalize(model); return sR > 0; } // 가로는 예산 초과 → 행 확장만(있었다면) 반영

  // 2) 붙임 영역과 겹치는 대상 병합 해제(먼저 수집 후 분할 — 반복 중 변형 회피).
  const region = expandRect(model, { r1: ar, c1: ac, r2: ar + sR - 1, c2: ac + sC - 1 });
  const toSplit = new Set();
  for (let r = region.r1; r <= region.r2; r++)
    for (let c = region.c1; c <= region.c2; c++) {
      const m = model.G[r][c];
      if (m && (m.rowspan > 1 || m.colspan > 1)) toSplit.add(m);
    }
  toSplit.forEach((m) => splitCell(model, m));

  // 3) 소스 셀을 대상에 복제(소스 병합은 공유 master 로 보존).
  const map = new Map();
  for (let r = 0; r < sR; r++)
    for (let c = 0; c < sC; c++) {
      const sm = src.G[r][c];
      let tm = map.get(sm);
      if (!tm) { tm = cloneMaster(sm); map.set(sm, tm); }
      model.G[ar + r][ac + c] = tm;
    }

  normalize(model);
  return true;
}
